import { format, parseISO } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../network/ApiCall';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css'
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import translation from '../../i18n/en';
import { Link } from 'react-router-dom';
import { PUBLIC_APP_URL } from '../../config.js';
import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

const ActivityLogGrid = () => {

  const { t } = useTranslation();

  const [gridRef, setGridRef] = useState(null);
  const gridStyle = { minHeight: 550 };
  const SEPARATOR = ',';

  const getData = async ({ skip, limit, sortInfo, filterValue }) => {
    const sortData = sortInfo ?? null;
    const url = "/activity"
    const result = await axiosInstance.get(url, {
      params: {
        start: skip,
        length: limit,
        filter: encodeURIComponent(JSON.stringify(filterValue)),
        sort: encodeURIComponent(JSON.stringify(sortData))
      }
    })

    const data = result.data.data;
    const total = result.data.recordsTotal;

    return { data, count: parseInt(total) }
  };

  const downloadBlob = (blob, fileName = t('activityLog:lblExportFileName') + '.csv') => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.position = 'absolute';
    link.style.visibility = 'hidden';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const exportCSV = () => {

    const columns = gridRef.current.visibleColumns;
    const header = columns.map((c) => c.name).join(SEPARATOR);
    const rows = gridRef.current.data.map((data) => {

      return columns.map((c) => {
        if (c.name === 'URL' && data.url)
          return PUBLIC_APP_URL + "/app" + data.url
        // Apply formatter if it exists
        if (c.render) {
          return c.render({ data });
        }
        // Otherwise return raw data
        return data[c.id];
      }).join(SEPARATOR)
    }
    );

    const contents = [header].concat(rows).join('\n');
    const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(blob);
  };


  const activityFilterSources = Object.keys(translation.activities).map((key) =>
    ({ id: key, label: t("activities:" + key + ":filter") }))

  const gridI18n = {...ReactDataGrid.defaultProps.i18n, "calendar.todayButtonText": t("common:lblToday")}

  const activityRenderer = useCallback(({ data }) => {
    let context = data.contextData;

    if (data.activityCode === "SEND_ETIR_MESSAGE" || data.activityCode === "RECEIVE_ETIR_MESSAGE")
      return t("activityLog:friendlyMessageTypes:" + data.contextData)

    return t("activities:" + data.activityCode + ":thirdPerson", { contextData: context })
  }, [t]);

  const columns = useMemo(() => [
    {
      header: t('activityLog:lblDateCreated'),
      name: 'dateCreated',
      sortable: true,
      flex: 1,
      enableColumnFilterContextMenu: false,
      filterEditor: DateFilter,
      filterEditorProps: (props, { index }) => {
        // for range and notinrange operators, the index is 1 for the after field
        return {
          dateFormat: 'DD/MM/YYYY',
          cancelButton: false,
          highlightWeekends: false,

        }
      },
      render: ({ data }) => format(parseISO(data.dateCreated, 'yyyy-MM-d'), 'd/MM/yyyy HH:mm')
    },
    {
      header: t('activityLog:lblUser'),
      name: 'username',
      sortable: true
    },
    {
      header: t('activityLog:lblActivity'),
      name: 'activityCode',
      sortable: true,
      flex: 1,
      enableColumnFilterContextMenu: false,
      render: activityRenderer,
      filterEditor: SelectFilter,
      filterEditorProps: {
        placeholder: 'All',
        dataSource: activityFilterSources
      },
    }, {
      header: t('activityLog:lblUrl'),
      name: 'url',
      sortable: true,
      flex: 1,
      render: ({ data }) => data.url && <Link to={PUBLIC_APP_URL + "/app" + data.url} target='_blank'>{data.url}</Link>
    },], [t, activityFilterSources, activityRenderer]);

  const filterValue = [
    { name: 'dateCreated', operator: 'eq', type: 'date', value: '' },
    { name: 'username', operator: 'contains', type: 'string', value: '' },
    { name: 'activityCode', operator: 'contains', type: 'string', value: '' },
    { name: 'url', operator: 'contains', type: 'string', value: '' }
  ];


  return (
    <div>
      <div className="table-responsive">

        <ReactDataGrid
          i18n={gridI18n}
          handle={setGridRef}
          style={gridStyle}
          columns={columns}
          dataSource={getData}
          defaultSortInfo={{ dir: 0, id: 'dateCreated', name: 'dateCreated' }}
          pagination
          defaultFilterValue={filterValue}
          showColumnMenuTool={false}
          defaultLimit={20}
        />

        <button className='mt-5 btn btn-outline-primary' onClick={exportCSV}>
          {t('activityLog:btnExport')}
        </button>
      </div>

    </div>
  );
};

export default ActivityLogGrid;
